import { useMemo, useState, type FormEvent } from "react"
import { motion } from "framer-motion"
import {Check, Copy} from 'lucide-react'

const UNIT_PRICE = 1280
const ORDER_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbwy-AILc98RyNqKdxvarg-0-I58MTCNow0o_96q30549zM0F8XptJ3ZR93jyJ6BOTc4/exec"

const BANK_INFO = [
  { label: "銀行代碼", value: "822" },
  { label: "銀行名稱", value: "中國信託商業銀行" },
  { label: "戶名", value: "零加壹文創整合有限公司" },
  { label: "帳號", value: "303540352643" },
]

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="flex items-center justify-between gap-4 px-6 py-6">
      <div className="flex flex-col gap-1.5">
        <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-400">
          {label}
        </span>
        <span className="font-serif-tc text-lg tracking-wide text-[#f7f1e6]">
          {value}
        </span>
      </div>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={`複製${label}`}
        className="flex h-10 w-10 shrink-0 items-center justify-center border border-white/15 text-stone-400 transition-colors duration-300 hover:border-[#c4a574] hover:text-[#c4a574]"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  )
}

function OrderForm() {
  const [quantity, setQuantity] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")

  const total = useMemo(() => quantity * UNIT_PRICE, [quantity])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (quantity === 0 || submitting) return

    const form = event.currentTarget
    const data = new FormData(form)
    const payload = {
      時間: new Date().toISOString(),
      商品: "馴養一朵風",
      版本: "CHAMPION EDITION 001",
      姓名: String(data.get("name") ?? "").trim(),
      手機號碼: String(data.get("phone") ?? "").trim(),
      Email: String(data.get("email") ?? "").trim(),
      數量: String(quantity),
      單價: String(UNIT_PRICE),
      訂購金額: String(total),
      匯款日期: String(data.get("transferDate") ?? "").trim(),
      匯款金額: String(data.get("transferAmount") ?? "").trim(),
      匯款帳號末五碼: String(data.get("lastFive") ?? "").trim(),
      收件人姓名: String(data.get("recipientName") ?? "").trim(),
      收件人手機: String(data.get("recipientPhone") ?? "").trim(),
      "7-ELEVEN門市名稱": String(data.get("storeName") ?? "").trim(),
      "7-ELEVEN門市地址": String(data.get("storeAddress") ?? "").trim(),
      備註: String(data.get("notes") ?? "").trim(),
    }

    setSubmitting(true)
    setSubmitError("")

    const body = JSON.stringify(payload)

    try {
      const response = await fetch(ORDER_SCRIPT_URL, {
        method: "POST",
        mode: "cors",
        redirect: "follow",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body,
      })

      const rawText = await response.text()

      if (!response.ok) {
        setSubmitError(
          `HTTP 狀態 ${response.status}，尚未確認試算表是否寫入。回傳內容：${rawText || "（空白）"}`,
        )
        return
      }

      let result: { success?: boolean; message?: string }
      try {
        result = JSON.parse(rawText) as { success?: boolean; message?: string }
      } catch {
        setSubmitError(
          `Google Apps Script 回傳不是有效 JSON，無法確認是否寫入試算表。原始內容：${rawText || "（空白）"}`,
        )
        return
      }

      if (result?.success === true) {
        form.reset()
        setQuantity(0)
        setSubmitted(true)
        return
      }

      setSubmitError(
        typeof result?.message === "string" && result.message.trim()
          ? result.message
          : "Apps Script 回傳 success 不為 true，試算表可能未寫入。",
      )
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error)
      setSubmitError(`無法連線 Google Apps Script（網路或 CORS）：${detail}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section id="order" className="relative w-full bg-[#0c0c0c] px-8 py-28 md:px-16 md:py-40 lg:px-24">
      <motion.p
        className="font-sans-ui text-center text-[11px] tracking-[0.48em] text-[#c4a574]"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 1 }}
      >
        Order
      </motion.p>

      <motion.h2
        className="font-display mt-6 text-center text-4xl text-[#f7f1e6] md:text-6xl"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 1.1, delay: 0.1 }}
      >
        立即訂購
      </motion.h2>

      <motion.p
        className="font-serif-tc mx-auto mt-6 w-full whitespace-nowrap text-center text-[11px] leading-7 text-stone-400 md:text-sm md:leading-8"
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 1.1, delay: 0.2 }}
      >
        請完成匯款後填寫以下資料，我們將依照付款順序安排出貨。
      </motion.p>

      <motion.div
        className="mx-auto mt-16 max-w-2xl"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1.2, delay: 0.2 }}
      >
        {submitted ? (
          <div className="border-t border-white/10 pt-10 text-center">
            <p className="font-display text-3xl text-[#f7f1e6]">
              送出成功
            </p>
            <p className="font-serif-tc mx-auto mt-4 max-w-md text-base leading-7 text-stone-400">
              訂單送出後，將寄送 Email 通知，
              <br />
              確認我們已收到您的訂單資訊。
              <br />
              完成付款確認後，將依訂單順序安排出貨。
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="border-t border-white/10 pt-10">
            {/* 訂購人資料 */}
            <h3 className="font-sans-ui text-center text-sm tracking-[0.32em] text-[#c4a574]">
              訂購人資料
            </h3>
            <div className="mt-10 grid gap-10 md:grid-cols-2">
              <label className="flex flex-col gap-3">
                <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">姓名</span>
                <input
                  required
                  name="name"
                  type="text"
                  autoComplete="name"
                  className="border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"
                />
              </label>

              <label className="flex flex-col gap-3">
                <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">手機號碼</span>
                <input
                  required
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  className="border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"
                />
              </label>

              <label className="flex flex-col gap-3">
                <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">Email</span>
                <input
                  required
                  name="email"
                  type="email"
                  autoComplete="email"
                  className="border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"
                />
              </label>

            </div>

            {/* 購買商品數量 */}
            <h3 className="font-sans-ui mt-20 text-center text-sm tracking-[0.32em] text-[#c4a574]">
              購買商品數量
            </h3>
            <div className="mt-10 flex items-center justify-between gap-6 border-b border-white/10 pb-8">
              <div>
                <p className="font-display text-2xl text-[#f7f1e6]">馴養一朵風</p>
                <p className="font-sans-ui mt-1 text-sm tracking-[0.18em] text-stone-500">
                  CHAMPION EDITION 001 · NT$1,280
                </p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setQuantity((value) => Math.max(0, value - 1))}
                  className="flex h-9 w-9 items-center justify-center border border-white/20 font-sans-ui text-stone-300 transition-colors duration-300 hover:border-[#c4a574] hover:text-[#c4a574]"
                  aria-label="減少數量"
                >
                  −
                </button>
                <span className="font-display w-8 text-center text-xl text-[#f7f1e6]">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((value) => value + 1)}
                  className="flex h-9 w-9 items-center justify-center border border-white/20 font-sans-ui text-stone-300 transition-colors duration-300 hover:border-[#c4a574] hover:text-[#c4a574]"
                  aria-label="增加數量"
                >
                  +
                </button>
              </div>
            </div>

            <p className="font-sans-ui mt-6 text-right text-[11px] leading-5 tracking-[0.18em] text-stone-500 md:whitespace-nowrap md:text-sm md:leading-6">
              ※ 售價 NT$1,280 已含 7-ELEVEN 店到店運費，無須另加。
            </p>

            {/* 匯款資訊 */}
            <h3 className="font-sans-ui mt-20 text-center text-sm tracking-[0.32em] text-[#c4a574]">
              匯款資訊
            </h3>
            <div className="mt-10 divide-y divide-white/10 border border-white/10 font-serif-tc text-sm text-stone-300">
              {BANK_INFO.map((item) => (
                <CopyField key={item.label} label={item.label} value={item.value} />
              ))}
            </div>
            <p className="font-sans-ui mt-4 text-center text-[11px] leading-6 tracking-[0.08em] text-stone-500 md:text-sm md:leading-7">
              售價已含運費，匯款金額請依訂購組數計算。
              <br />
              請先完成匯款後再填寫以下資料。
            </p>
            <p className="font-sans-ui mt-4 text-center text-[11px] leading-6 tracking-[0.08em] text-[#c4a574] md:text-sm md:leading-7">
              ⚠️ 溫馨提示：轉帳時請勿填寫「備註」，以利專人為您快速核對帳款與安排出貨。
            </p>

            {/* 匯款確認 */}
            <h3 className="font-sans-ui mt-20 text-center text-sm tracking-[0.32em] text-[#c4a574]">
              匯款確認
            </h3>
            <div className="mt-10 grid gap-10 md:grid-cols-2">
              <label className="flex flex-col gap-3">
                <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">匯款日期</span>
                <input
                  required
                  name="transferDate"
                  type="date"
                  className="border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"
                />
              </label>

              <label className="flex flex-col gap-3">
                <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">匯款金額</span>
                <input
                  required
                  name="transferAmount"
                  type="number"
                  min={0}
                  className="border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"
                />
              </label>

              <label className="flex flex-col gap-3 md:col-span-2">
                <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">匯款帳號末五碼</span>
                <input
                  required
                  name="lastFive"
                  type="text"
                  maxLength={5}
                  className="border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"
                />
              </label>
            </div>

            {/* 7-ELEVEN 店到店資訊 */}
            <h3 className="font-sans-ui mt-20 text-center text-sm tracking-[0.32em] text-[#c4a574]">
              7-ELEVEN 店到店資訊
            </h3>
            <div className="mt-10 grid gap-10 md:grid-cols-2">
              <label className="flex flex-col gap-3">
                <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">收件人姓名</span>
                <input
                  required
                  name="recipientName"
                  type="text"
                  autoComplete="name"
                  className="border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"
                />
              </label>

              <label className="flex flex-col gap-3">
                <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">收件人手機</span>
                <input
                  required
                  name="recipientPhone"
                  type="tel"
                  autoComplete="tel"
                  className="border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"
                />
              </label>

              <label className="flex flex-col gap-3">
                <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">7-ELEVEN 門市名稱</span>
                <input
                  required
                  name="storeName"
                  type="text"
                  className="border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"
                />
              </label>

              <label className="flex flex-col gap-3">
                <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">7-ELEVEN 門市地址</span>
                <input
                  required
                  name="storeAddress"
                  type="text"
                  className="border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"
                />
              </label>
            </div>

            {/* 備註 */}
            <h3 className="font-sans-ui mt-20 text-center text-sm tracking-[0.32em] text-[#c4a574]">
              備註
            </h3>
            <p className="font-serif-tc mx-auto mt-6 max-w-md text-center text-sm leading-7 text-stone-400">
              訂單送出後，將寄送 Email 通知，
              <br />
              確認我們已收到您的訂單資訊。
              <br />
              完成付款確認後，
              <br />
              將依訂單順序安排出貨。
            </p>

            <label className="mt-8 flex flex-col gap-2">
              <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-400">其他備註（選填）</span>
              <textarea
                name="notes"
                rows={3}
                className="resize-none border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"
              />
            </label>

            <div className="mt-10 flex flex-col items-center gap-4 border-t border-white/10 pt-10">
              <div className="font-sans-ui flex items-center gap-3 text-sm tracking-[0.28em] text-stone-500">
                <span>訂購金額</span>
                <span className="font-display text-xl text-[#c4a574]">
                  NT$ {total.toLocaleString()}
                </span>
              </div>

              {submitError ? (
                <p className="font-serif-tc text-center text-sm leading-6 text-red-400">
                  {submitError}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={quantity === 0 || submitting}
                className="font-sans-ui mt-2 border border-[#c4a574]/60 px-10 py-4 text-sm tracking-[0.32em] text-[#c4a574] transition-colors duration-500 hover:bg-[#c4a574] hover:text-[#080808] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#c4a574]"
              >
                {submitting ? "送出中…" : "確認送出訂單"}
              </button>

              <p className="font-sans-ui text-[11px] tracking-[0.18em] text-stone-600">
                送出前請確認所有資料正確無誤
              </p>
            </div>
          </form>
        )}
      </motion.div>
    </section>
  )
}

export default OrderForm
